import { readyAudioStatusSet } from '@listening/api-contract/audio-statuses';
import {
  NotFoundError,
  UnauthorizedError,
} from '@listening/api-contract/errors';
import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';
import {
  AudioConversionDetail,
  AudioConversionSummary,
} from '@listening/api-contract/schemas';
import { AsyncResult, Atom, type AtomRegistry } from 'effect/reactivity';
import { Duration, Effect, Schema } from 'effect';
import { apiQuery, ssrApiQuery } from './api-runtime';
import { AudioItems } from './audio-items';
import { audioKeys, audioRowAtom, writeAudios } from './audio-store';

// Per-audio reads, shared across apps (start / mobile / browser-ext). Each
// stores the summary it reads in the audio store and shows the store's copy
// over its own, so an optimistic edit re-renders the player directly.

// The detail payload (chapters / voice / alignment) is near-immutable once a
// conversion completes, so it is cached hard: within a session it is
// effectively never refetched, and the SSR `/doc/$docId` route does not
// refetch the server-rendered detail on hydration. Edits stay current through
// the store; the finite window only self-heals out-of-band backend changes (a
// re-synthesis or a data fix the client never triggered).
const audioStaleTime = Duration.hours(24);

const detailQueryAtom = Atom.family((audioId: string) =>
  ssrApiQuery(
    AudioItems.detailedEffect(audioId).pipe(
      Effect.tap((detail) => writeAudios([detail])),
    ),
    {
      key: `audio-detail:${audioId}`,
      success: AudioConversionDetail,
      staleTime: audioStaleTime,
    },
  ),
);

/** `/v2/audio/{id}/detailed`: the summary plus chapters and voice. Writing a
 * detail replaces the cached one until the next fetch. */
export const audioDetailAtom = Atom.family((audioId: string) =>
  Atom.transform(
    detailQueryAtom(audioId),
    (get, detailQuery) =>
      AsyncResult.map(get(detailQuery), (detail) => {
        const row = get(audioRowAtom(audioId));
        return row ? { ...detail, ...row } : detail;
      }),
    { initialValueTarget: detailQueryAtom(audioId) },
  ),
);

const summaryQueryAtom = Atom.family((audioId: string) =>
  ssrApiQuery(
    AudioItems.detailEffect(audioId).pipe(
      Effect.tap((summary) => writeAudios([summary])),
    ),
    {
      key: `audio-summary:${audioId}`,
      success: AudioConversionSummary,
      staleTime: audioStaleTime,
      reactivityKeys: [audioKeys.summaries],
    },
  ),
);

/** `/v2/audio/{id}`: one audio's summary, without chapters. */
export const audioSummaryAtom = Atom.family((audioId: string) =>
  Atom.transform(
    summaryQueryAtom(audioId),
    (get, summaryQuery) =>
      AsyncResult.map(
        get(summaryQuery),
        (summary) => get(audioRowAtom(audioId)) ?? summary,
      ),
    { initialValueTarget: summaryQueryAtom(audioId) },
  ),
);

/** Refetches the detail and summary of each of `audioIds` that a screen has
 * loaded; an audio nothing holds is left to fetch when it is next read. One
 * audio is refreshed through its atoms because a reactivity key cannot target
 * it: `{ audio: [id] }` also reaches every other audio's queries. */
export const refreshAudios = (
  registry: AtomRegistry.AtomRegistry,
  audioIds: Iterable<string>,
) => {
  for (const audioId of audioIds) {
    registry.refresh(detailQueryAtom(audioId));
    registry.refresh(summaryQueryAtom(audioId));
  }
};

/** Page image metadata is immutable, and a long document has many pages, so
 * it is never refetched and is dropped two minutes after it leaves the
 * screen. */
export const audioPageImageAtom = Atom.family(
  ({ audioId, page }: { readonly audioId: string; readonly page: number }) =>
    apiQuery(AudioItems.pageImageEffect(audioId, page), {
      staleTime: Duration.infinity,
      timeToLive: Duration.minutes(2),
    }),
);

const isNotFound = Schema.is(NotFoundError);
const isUnauthorized = Schema.is(UnauthorizedError);

/** The audio detail fetch failed. Never fatal on its own — the caller decides
 * what a still-missing detail means. */
export class AudioDetailFetchError extends Schema.TaggedError<AudioDetailFetchError>()(
  'AudioDetailFetchError',
  {
    audioId: Schema.String,
    cause: Schema.Unknown,
  },
) {
  /** The server answered that this viewer cannot have the document — it does
   * not exist, was deleted, or is someone else's private document — so
   * retrying cannot help. Only an explicit `access-denied` 401 counts: a
   * refusal for want of a session (an expired sign-in) or one that gives no
   * reason stays retryable, since signing in again may open it. */
  get documentUnavailable() {
    return (
      isNotFound(this.cause) ||
      (isUnauthorized(this.cause) && this.cause.reason === 'access-denied')
    );
  }
}

/** The conversion has produced enough for playback to start. */
export const isAudioDetailReadyForStreaming = (
  audioItemDetail: Pick<AudioItemDetail, 'conversion_status'>,
) => readyAudioStatusSet.has(audioItemDetail.conversion_status);
